<template>
  <v-container fluid class="nw-page">
    <v-card class="nw-card" flat>
      <div class="d-flex justify-space-between align-start">
        <div>
          <div class="nw-title">New Activity</div>
          <div class="nw-subtitle">
            Add details about your work activity. Fields marked with <span class="nw-req">*</span> are required.
          </div>
        </div>
        <v-btn icon small to="/my-activity" class="nw-close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <div class="nw-grid2 mt-4">
        <!-- Employee -->
        <div>
          <label class="nw-label">Employee <span class="nw-req">*</span></label>
          <v-autocomplete v-model="dataItem.userId" :items="Emp_list" item-text="name" item-value="id" outlined dense
            hide-details disabled append-icon="mdi-chevron-down" class="nw-input"></v-autocomplete>
        </div>

        <!-- Assignment / Case -->
        <div>
          <label class="nw-label">Assignment / Case <span class="nw-req">*</span></label>
          <v-autocomplete v-model="dataItem.jobtitle_id" :items="jobtitle_list" item-text="name" item-value="id"
            outlined dense hide-details append-icon="mdi-chevron-down" class="nw-input"></v-autocomplete>
        </div>

        <!-- Client -->
        <div>
          <label class="nw-label">Client <span class="nw-req">*</span></label>
          <v-autocomplete v-model="dataItem.customer_id" :items="customer_list" item-text="name" item-value="id"
            outlined dense hide-details clearable append-icon="mdi-chevron-down" class="nw-input" @click:clear="
              $nextTick(() => {
                dataItem.customer_id = 0;
              })
              "></v-autocomplete>
        </div>

        <!-- Charge Type -->
        <div>
          <label class="nw-label">Charge Type <span class="nw-req">*</span></label>
          <v-autocomplete v-model="dataItem.chargetype" :items="charge_list" item-text="name" item-value="value"
            outlined dense hide-details append-icon="mdi-chevron-down" class="nw-input"></v-autocomplete>
        </div>
      </div>

      <!-- Date / Start / End -->
      <div class="nw-grid-time mt-4">
        <div>
          <label class="nw-label">Date <span class="nw-req">*</span></label>
          <v-menu v-model="menuDateCheckin" :close-on-content-click="false" transition="scale-transition" offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-model="computedDateCheckInFormatted" append-icon="mdi-calendar-today-outline" readonly
                v-bind="attrs" v-on="on" outlined dense hide-details class="nw-input"></v-text-field>
            </template>
            <v-date-picker v-model="dataItem.checkin" :max="new Date(
              Date.now() - new Date().getTimezoneOffset() * 60000,
            ).toISOString().substr(0, 10)" :min="minDateAllowed" :allowed-dates="allowedDates"
              @input="menuDateCheckin = false" @change="getWorkHourDuration()" />
          </v-menu>
        </div>

        <div>
          <label class="nw-label">Start Time <span class="nw-req">*</span></label>
          <div class="nw-time-box" :class="{ 'nw-time-box--disabled': isDisabledTimeField }">
            <datetime class="nw-dt" type="time" input-id="menutimeadd" value-zone="Asia/Bangkok" format="HH:mm"
              placeholder="--:--" v-model="dataItem.timecheckin" :max-datetime="getMaxTime()"
              :disabled="isDisabledTimeField" @input="getWorkHourDuration" />
            <v-icon class="nw-time-icon" small>mdi-clock-outline</v-icon>
          </div>
        </div>

        <div>
          <label class="nw-label">End Time <span class="nw-req">*</span></label>
          <div class="nw-time-box" :class="{ 'nw-time-box--disabled': isDisabledTimeFieldCheckOut }">
            <datetime class="nw-dt" type="time" input-id="menutimeaddout" value-zone="Asia/Bangkok" format="HH:mm"
              placeholder="--:--" v-model="dataItem.timecheckout" :max-datetime="getMaxTimeCheckOut()"
              :disabled="isDisabledTimeFieldCheckOut" @input="getWorkHourDuration()"></datetime>
            <v-icon class="nw-time-icon" small>mdi-clock-outline</v-icon>
          </div>
        </div>

        <div class="nw-duration">
          <template v-if="showDuration != ''">
            <span class="nw-duration-label">Duration:</span>
            <span :class="showDuration != 'Start date/time must be before End date/time'
              ? 'nw-ok'
              : 'nw-err'
              ">{{ showDuration }}</span>
          </template>
        </div>
      </div>

      <!-- Details -->
      <div class="mt-4">
        <label class="nw-label">Details</label>
        <v-textarea v-model="dataItem.detail" placeholder="Add activity details, notes, or description..." outlined
          hide-details rows="3" no-resize class="nw-input nw-textarea"></v-textarea>
        <div class="nw-counter">{{ (dataItem.detail || "").length.toLocaleString() }}/1,000</div>
      </div>

      <!-- Footer -->
      <div class="d-flex justify-space-between align-center mt-2">
        <v-checkbox v-model="saveAndAddAnother" label="Save and add another" hide-details dense
          class="nw-check mt-0 pt-0"></v-checkbox>
        <div class="d-flex" style="gap: 12px">
          <v-btn to="/my-activity" depressed outlined height="44" class="nw-btn-cancel">Cancel</v-btn>
          <v-btn depressed height="44" class="nw-btn-save" :loading="isSaving" @click="save()">Save</v-btn>
        </div>
      </div>
    </v-card>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />

    <SuccessDialogPush :status="dialogAdd" :text_color="text_color" :title="title" :message="message" :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import StockCard from "@/components/cards/StockCard";
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import {
  tolocalestringnumber,
  stringcommatonumber,
} from "../computing/tolocalestringnumber";
import unity from "@/unity/unity";
import { shadow } from "pdfjs-dist";
const moment = require("moment");

export default {
  name: "New Activity",
  data() {
    return {
      isSaving: false,
      saveAndAddAnother: false,
      dialogAdd: false,
      dialogSendMail: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      link: "",
      Emp_list: [],
      jobtitle_list: [],
      charge_list: [
        { value: "T/C", name: "Time Based" },
        { value: "N/C", name: "Non-Billable" },
        { value: "F/C", name: "Fixed Fee" },
        { value: "OFF", name: "Internal Office Work" },
      ],
      customer_list: [],
      menuDateCheckin: false,
      menuDateCheckout: false,
      dataItem: {
        userId: Number(localStorage.getItem(server.USER_ID)),
        customer_id: 0,
        jobtitle_id: 0,
        // checkin: "",
        // checkout: "",
        checkin: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        checkout: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        timecheckin: "",
        timecheckout: "",
        timecheckinadd: "",
        timecheckoutadd: "",
        hours: "",
        chargetype: "",
        prospect: "",
        detail: "",
        durationhour: 0,
        durationminute: 0,
      },
      statusActivity: "",
      checklate: false,
      update_id: 0,
      showDuration: "",
    };
  },
  components: {
    StockCard,
    SuccessDialog,
    SuccessDialogPush,
  },
  computed: {
    computedDateCheckInFormatted() {
      return unity.formatDate(this.dataItem.checkin);
    },
    computedDateCheckOutFormatted() {
      return unity.formatDate(this.dataItem.checkout);
    },
    minDateAllowed() {
      return moment()
        .tz("Asia/Bangkok")
        .subtract(7, "days")
        .format("YYYY-MM-DD");
    },
    isTodayCheckin() {
      const selected = new Date(this.dataItem.checkin);
      const today = new Date();
      return (
        selected.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)
      );
    },
    isDisabledTimeField() {
      return this.isTodayCheckin && this.isPastCurrentTimeLimit();
    },
    isTodayCheckOut() {
      const selected = new Date(this.dataItem.checkout);
      const today = new Date();
      return (
        selected.toISOString().slice(0, 10) === today.toISOString().slice(0, 10)
      );
    },
    isDisabledTimeFieldCheckOut() {
      return this.isTodayCheckOut && this.isPastCurrentTimeLimitCheckOut();
    },
  },

  watch: {
    "dataItem.checkin"(newVal) {
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (newVal === today && this.dataItem.timecheckin) {
        const time = moment.tz(this.dataItem.timecheckin, "Asia/Bangkok");
        const now = moment.tz("Asia/Bangkok");

        if (time.isAfter(now)) {
          this.dataItem.timecheckin = null;
        }
      }
    },
    "dataItem.checkout"(newVal) {
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (newVal === today && this.dataItem.timecheckout) {
        const time = moment.tz(this.dataItem.timecheckout, "Asia/Bangkok");
        const now = moment.tz("Asia/Bangkok");

        if (time.isAfter(now)) {
          this.dataItem.timecheckout = null;
        }
      }
    },
  },

  async mounted() {
    await api.checkVersion();

    this.$forceUpdate();
    const userId = localStorage.getItem(server.USER_ID);
    if (userId && api.isLoggedIn()) {
      // this.$router.push("/");
    } else {
      this.$store.state.isLogged = false;
      this.$router.push("/login");
    }
    this.loadEmp();
    this.loadCustomer();
    this.loadJobtitle();
    this.$hideLoader();
  },
  methods: {
    allowedDates(val) {
      const day = new Date(val).getDay();
      return day !== 0 && day !== 6; // 0 = อาทิตย์, 6 = เสาร์
    },
    getMaxTime() {
      const selected = moment
        .tz(this.dataItem.checkin, "Asia/Bangkok")
        .format("YYYY-MM-DD");
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (selected === today) {
        return moment.tz("Asia/Bangkok").format("HH:mm");
      }
      return null;
    },
    getMaxTimeCheckOut() {
      const selected = moment
        .tz(this.dataItem.checkout, "Asia/Bangkok")
        .format("YYYY-MM-DD");
      const today = moment.tz("Asia/Bangkok").format("YYYY-MM-DD");

      if (selected === today) {
        return moment.tz("Asia/Bangkok").format("HH:mm");
      }
      return null;
    },
    isPastCurrentTimeLimit() {
      // Optional: ใช้ได้ถ้าต้องการเงื่อนไขซับซ้อนกว่านี้
      return false;
    },
    isPastCurrentTimeLimitCheckOut() {
      // Optional: ใช้ได้ถ้าต้องการเงื่อนไขซับซ้อนกว่านี้
      return false;
    },
    async loadEmp() {
      const result = await api.getAccountslist_by_ComID(
        localStorage.getItem(server.COMPANYID),
      );

      this.Emp_list = result.data;
      this.dataItem.userId = localStorage.getItem(server.USER_ID);
    },
    async loadCustomer() {
      const result = await api.getAllCustomerListactiveCompany({
        params: {
          company_id: localStorage.getItem(server.COMPANYID),
        },
      });
      this.customer_list = result.data;
    },
    async loadJobtitle() {
      const result = await api.getAllJobtitleByDepartment(
        {
          params: {
            company_id: localStorage.getItem(server.COMPANYID),
            department: localStorage.getItem(server.DEPARTMENT_ID),
          },
        }
      );

      this.jobtitle_list = result.data;
    },

    async getWorkHourDuration() {
      if (this.dataItem.checkout != "") {
        this.dataItem.checkout = this.dataItem.checkin;

        if (
          this.dataItem.timecheckin !== "" &&
          this.dataItem.timecheckout !== ""
        ) {
          // ดึงเฉพาะเวลา (HH:mm) จาก string ISO
          const checkinTime = moment(this.dataItem.timecheckin).format("HH:mm");
          const checkoutTime = moment(this.dataItem.timecheckout).format(
            "HH:mm",
          );

          // สร้าง moment ใหม่โดยใช้วันที่เดียวกัน เช่น 2000-01-01 เพื่อไม่ให้วันมีผล
          const baseDate = "2000-01-01";
          const checkin = moment(
            `${baseDate} ${checkinTime}`,
            "YYYY-MM-DD HH:mm",
          );
          const checkout = moment(
            `${baseDate} ${checkoutTime}`,
            "YYYY-MM-DD HH:mm",
          );

          if (checkin.isSameOrAfter(checkout)) {
            this.showDuration = "Start date/time must be before End date/time";
            return;
          }

          let totalMinutes = checkout.diff(checkin, "minutes");

          // ถ้า checkout < checkin แปลว่าเวลาข้ามคืน
          if (totalMinutes < 0) {
            totalMinutes += 1440; // เพิ่ม 24 ชั่วโมง (1440 นาที)
          }

          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          if (hours > 0) {
            if (minutes > 0) {
              this.showDuration = `${hours} Hours ${minutes} Minute`;
            } else {
              this.showDuration = `${hours} Hours`;
            }
          } else {
            this.showDuration = `${minutes} Minute`;
          }
        }
      }
    },
    async save() {
      if (this.isSaving) return; // ถ้ากำลัง save อยู่ หยุดการทำงาน
      this.isSaving = true;      // 🔹 ตั้ง flag

      this.$showLoader();

      // --- ตรวจสอบ field จำเป็น ---
      const checkField = (field, fieldName) => {
        if (!field) {
          this.$hideLoader();
          this.isSaving = false; // 🔹 reset flag
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Check your information again",
            `กรุณากรอกรายละเอียด ${fieldName}`,
            "text-h5 red--text text-center",
          );
          return false;
        }
        return true;
      };

      const requiredFields = [
        { field: this.dataItem.userId, name: "Employee" },
        { field: this.dataItem.jobtitle_id, name: "Assignment" },
        { field: this.dataItem.checkin, name: "Date" },
        { field: this.dataItem.timecheckin, name: "Start Time" },
        { field: this.dataItem.timecheckout, name: "End Time" },
        { field: this.dataItem.chargetype, name: "Charge-Type" },
        { field: this.dataItem.detail, name: "Details" },
      ];

      for (let item of requiredFields) {
        if (!checkField(item.field, item.name)) return;
      }

      // --- ตรวจสอบเวลา ---
      const baseDate = "2000-01-01";
      const checkin = moment(`${baseDate} ${moment(this.dataItem.timecheckin).format("HH:mm")}`, "YYYY-MM-DD HH:mm");
      const checkout = moment(`${baseDate} ${moment(this.dataItem.timecheckout).format("HH:mm")}`, "YYYY-MM-DD HH:mm");

      const checkinDateTime = moment(
        `${this.dataItem.checkin} ${moment(this.dataItem.timecheckin).format("HH:mm:ss")}`,
        "YYYY-MM-DD HH:mm:ss"
      ).add(7, 'hours');

      if (checkin.isSameOrAfter(checkout)) {
        this.$hideLoader();
        this.isSaving = false; // 🔹 reset flag
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "Start date/time must be before End date/time",
          "text-h5 red--text text-center",
        );
        return;
      }

      // --- เตรียม data ส่ง API ---
      const totalMinutes = checkout.diff(checkin, "minutes");
      const addItem = {
        chargeType: this.dataItem.chargetype,
        company_id: Number(localStorage.getItem(server.COMPANYID)),
        checkInDate: checkinDateTime.format("YYYY-MM-DD HH:mm:ss"),
        checkOutDate: `${this.dataItem.checkout} ${moment(this.dataItem.timecheckout).format("HH:mm:ss")}`,
        isCheckedOut: true,
        userId: this.dataItem.userId,
        departmentId: Number(localStorage.getItem(server.DEPARTMENT_ID)),
        jobTitleId: this.dataItem.jobtitle_id,
        customerId: this.dataItem.customer_id || null,
        prospect: this.dataItem.prospect || null,
        // detail: this.dataItem.detail.replace(/'/g, "\\'"),
        detail: this.dataItem.detail,
        duration: Number(totalMinutes),
        addby: "W",
        check_auto: 0,
        status: "A",
        type: "A"
      };

      try {
        const res_updateAct = await api.createActivityEmp(addItem);

        this.$hideLoader();
        this.isSaving = false; // 🔹 reset flag

        if (
          (res_updateAct.status === 200 || res_updateAct.status === 201) &&
          this.saveAndAddAnother
        ) {
          // Save and add another: แจ้งบันทึกสำเร็จ แล้วล้างฟอร์มเพื่อกรอกรายการถัดไป (ไม่เปลี่ยนหน้า)
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Information Saved",
            "Information successfully saved",
            "text-h5 green--text text-center",
          );
          this.resetForNext();
        } else if (res_updateAct.status === 200 || res_updateAct.status === 201) {
          this.$store.state.global_push_dialog = true;
          this.setupAlertDialogPush(
            true,
            "Information Saved",
            "Information successfully saved",
            "text-h5 green--text text-center",
            "/my-activity",
          );
        } else {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Information Saved",
            "ไม่สามารถบันทึกได้",
            "text-h5 red--text text-center",
          );
        }
      } catch (error) {
        this.$hideLoader();
        this.isSaving = false; // 🔹 reset flag
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึกข้อมูล",
          "text-h5 red--text text-center",
        );
        console.error(error);
      }
    },
    resetForNext() {
      // คง Employee / Assignment / Client / Charge Type / Date ไว้ ล้างเฉพาะเวลาและรายละเอียด
      this.dataItem.timecheckin = "";
      this.dataItem.timecheckout = "";
      this.dataItem.detail = "";
      this.showDuration = "";
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.link = link;
    },
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
  },
  async beforeCreate() {
    console.log("beforeCreate");
    this.$store.state.navMenu = true;
  },
  async created() { },
};
</script>

<style scoped>
.nw-page { background: #f4f6f9; }
.nw-card { border-radius: 12px !important; padding: 26px 32px 22px; }
.nw-title { font-size: 24px; font-weight: 800; color: #111827; line-height: 1.2; }
.nw-subtitle { font-size: 14px; color: #6b7280; margin-top: 6px; }
.nw-close .v-icon { color: #111827 !important; }
.nw-req { color: #e53935; }
.nw-label { display: block; font-size: 14px; font-weight: 700; color: #374151; margin-bottom: 6px; }

.nw-grid2 { display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; row-gap: 16px; }
.nw-grid-time { display: grid; grid-template-columns: 24% 22% 19% 1fr; column-gap: 28px; align-items: end; }

.nw-input >>> .v-input__slot { min-height: 42px !important; border-radius: 6px; background: #fff !important; }
.nw-input >>> fieldset { border-color: #d1d5db; }
.nw-input >>> .v-input__append-inner { margin-top: 9px !important; }
.nw-input >>> .v-input__append-inner .v-icon { color: #374151; font-size: 20px; }
.nw-input >>> input { color: #111827; font-size: 14px; }
.nw-input.v-input--is-disabled >>> .v-input__slot { background: #fff !important; }
.nw-input.v-input--is-disabled >>> input,
.nw-input.v-input--is-disabled >>> .v-select__selection { color: #111827 !important; }
.nw-input >>> input::placeholder,
.nw-input >>> textarea::placeholder { color: #9ca3af; }
.nw-textarea >>> .v-input__slot { min-height: 86px !important; }
.nw-textarea >>> textarea { font-size: 14px; }
.nw-counter { text-align: right; font-size: 13px; color: #6b7280; margin-top: 4px; }

.nw-time-box { position: relative; height: 42px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; }
.nw-time-box:hover { border-color: #1f2d4d; }
.nw-time-box--disabled { background: #f3f4f6; }
.nw-time-icon { position: absolute !important; right: 12px; top: 12px; pointer-events: none; color: #374151 !important; }
.nw-dt { height: 100%; }
.nw-dt >>> input { width: 100%; height: 40px; padding: 0 36px 0 16px; border: none; outline: none; background: transparent; cursor: pointer; font-size: 14px; color: #111827; }
.nw-dt >>> input::placeholder { color: #9ca3af; }

.nw-duration { min-height: 42px; display: flex; align-items: center; gap: 8px; font-size: 14px; }
.nw-duration-label { font-weight: 700; color: #374151; }
.nw-ok { color: #16a34a; font-weight: 600; }
.nw-err { color: #dc2626; font-weight: 600; }

.nw-check >>> .v-label { font-size: 14px; color: #374151; }
.nw-btn-save { background-color: #1f2d4d !important; color: #fff !important; text-transform: none !important; letter-spacing: 0 !important; font-weight: 600; border-radius: 6px; min-width: 84px !important; }
.nw-btn-cancel { border-color: #d1d5db !important; color: #111827 !important; text-transform: none !important; letter-spacing: 0 !important; font-weight: 600; border-radius: 6px; min-width: 98px !important; }

@media (max-width: 960px) {
  .nw-card { padding: 20px 16px; }
  .nw-grid2 { grid-template-columns: 1fr; }
  .nw-grid-time { grid-template-columns: 1fr 1fr; row-gap: 14px; column-gap: 14px; }
}
</style>